import { Trash2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

const BUTTON_VARIANTS = [
  'default',
  'secondary',
  'destructive',
  'outline',
  'ghost',
  'link',
] as const;
const BUTTON_SIZES = ['default', 'sm', 'lg', 'icon'] as const;
const BADGE_VARIANTS = ['default', 'secondary', 'destructive', 'outline', 'success'] as const;

const FORM_CONTROL_STATES = ['resting', 'autofocus', 'disabled', 'aria-invalid'] as const;
type ControlState = (typeof FORM_CONTROL_STATES)[number];

function stateProps(state: ControlState): Record<string, unknown> {
  if (state === 'autofocus') return { autoFocus: true };
  if (state === 'disabled') return { disabled: true };
  if (state === 'aria-invalid') return { 'aria-invalid': true };
  return {};
}

function buttonLabel(
  variant: (typeof BUTTON_VARIANTS)[number],
  size: (typeof BUTTON_SIZES)[number],
) {
  if (size === 'icon') return null;
  if (size === 'default') return variant;
  return `${variant} · ${size}`;
}

export function ButtonsSection() {
  return (
    <section className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Buttons</h2>
        <p className="text-muted-foreground mt-1 text-sm">
          Six variants × four sizes. Hover and touch-active both darken via the same opacity stop;
          focus shows a mint ring outside the control via{' '}
          <code className="font-mono">ring-offset-2</code>.
        </p>
      </div>
      <div className="space-y-3">
        {BUTTON_VARIANTS.map((variant) => (
          <div key={variant} className="flex flex-wrap items-center gap-3">
            <span className="text-muted-foreground w-24 shrink-0 font-mono text-xs">{variant}</span>
            {BUTTON_SIZES.map((size) => (
              <Button key={size} variant={variant} size={size}>
                {size === 'icon' ? <Trash2 aria-hidden /> : buttonLabel(variant, size)}
              </Button>
            ))}
          </div>
        ))}
      </div>
      <DestructiveActionPatterns />
    </section>
  );
}

function DestructiveActionPatterns() {
  return (
    <div className="space-y-3 border-t border-border pt-4">
      <div>
        <h3 className="text-foreground text-sm font-semibold">Destructive actions</h3>
        <p className="text-muted-foreground mt-1 text-sm">
          Two documented forms. Reserve the solid <code className="font-mono">destructive</code>{' '}
          variant for the button that <strong>commits</strong> the destructive mutation (typically
          inside a confirmation dialog) — one per flow. Use the ghost form with{' '}
          <code className="font-mono">text-destructive</code> for the row-level{' '}
          <strong>affordance</strong> that opens that dialog. The persistent red color carries the
          intent without painting every grid row as a solid red box.
        </p>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <DestructivePatternCard
          label="Affordance — opens dialog"
          snippet={
            '<Button\n  variant="ghost"\n  size="icon"\n  className="text-destructive\n    hover:text-destructive size-8"\n/>'
          }
          example={
            <Button
              variant="ghost"
              size="icon"
              className="text-destructive hover:text-destructive size-8"
              aria-label="Delete (affordance example)"
            >
              <Trash2 className="size-4" aria-hidden />
            </Button>
          }
        />
        <DestructivePatternCard
          label="Commit — fires the mutation"
          snippet={'<Button variant="destructive">\n  Delete\n</Button>'}
          example={<Button variant="destructive">Delete</Button>}
        />
      </div>
    </div>
  );
}

function DestructivePatternCard({
  label,
  snippet,
  example,
}: Readonly<{ label: string; snippet: string; example: React.ReactNode }>) {
  return (
    <div className="border-border bg-card space-y-3 rounded-md border p-3">
      <p className="text-muted-foreground font-mono text-xs">{label}</p>
      <div className="flex h-12 items-center">{example}</div>
      <pre className="bg-muted text-foreground overflow-x-auto rounded p-2 font-mono text-xs leading-relaxed">
        {snippet}
      </pre>
    </div>
  );
}

export function FormControlsSection() {
  return (
    <section className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Form controls</h2>
        <p className="text-muted-foreground mt-1 text-sm">
          <code className="font-mono">input</code>, <code className="font-mono">textarea</code>, and{' '}
          <code className="font-mono">select</code> render at 16 px on mobile (avoiding iOS
          auto-zoom) and step down to 14 px at <code className="font-mono">md</code> and up.
        </p>
        <p className="text-muted-foreground mt-2 text-sm">
          The native <code className="font-mono">select</code> below stays available for legacy
          callers, but new code should reach for <code className="font-mono">Combobox</code> /{' '}
          <code className="font-mono">ComboboxField</code> instead — see the section below. The
          combobox keeps a uniform look across plain dropdowns, long enum pickers, and async
          server-driven search, and it opens as a bottom sheet on mobile so the on-screen keyboard
          never collides with the menu.
        </p>
      </div>
      <ControlGrid label="Input" Control={InputCell} />
      <ControlGrid label="Textarea" Control={TextareaCell} />
      <ControlGrid label="Select (native — prefer Combobox)" Control={SelectCell} />
    </section>
  );
}

function ControlGrid({
  label,
  Control,
}: Readonly<{ label: string; Control: React.ComponentType<{ state: ControlState }> }>) {
  return (
    <div className="space-y-3">
      <h3 className="text-foreground text-sm font-semibold">{label}</h3>
      <div className="grid max-w-xl grid-cols-1 gap-3 sm:grid-cols-2">
        {FORM_CONTROL_STATES.map((state) => (
          <label key={state} className="space-y-1">
            <span className="text-muted-foreground font-mono text-xs">{state}</span>
            <Control state={state} />
          </label>
        ))}
      </div>
    </div>
  );
}

function InputCell({ state }: Readonly<{ state: ControlState }>) {
  const props = state === 'aria-invalid' ? { defaultValue: '€-12.34' } : { placeholder: state };
  return <Input {...props} {...stateProps(state)} />;
}

function TextareaCell({ state }: Readonly<{ state: ControlState }>) {
  const props =
    state === 'aria-invalid' ? { defaultValue: 'Reason cannot be empty.' } : { placeholder: state };
  return <Textarea rows={3} {...props} {...stateProps(state)} />;
}

function SelectCell({ state }: Readonly<{ state: ControlState }>) {
  return (
    <Select defaultValue={state === 'aria-invalid' ? '' : 'eur'} {...stateProps(state)}>
      <option value="" disabled>
        Choose a currency
      </option>
      <option value="eur">EUR — euro</option>
      <option value="usd">USD — US dollar</option>
      <option value="gbp">GBP — pound sterling</option>
    </Select>
  );
}

export function BadgesSection() {
  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Badges</h2>
        <p className="text-muted-foreground mt-1 text-sm">
          Five variants. Use <code className="font-mono">success</code> for completed states and
          <code className="font-mono"> destructive</code> for errors; the rest are neutral.
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {BADGE_VARIANTS.map((variant) => (
          <Badge key={variant} variant={variant}>
            {variant}
          </Badge>
        ))}
      </div>
    </section>
  );
}
